import { useState } from "react";
import { LayoutDashboard, RefreshCw, Loader2, Wifi } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Section } from "./Section";

export function BrandingSettings({ t, input, isAdmin, settings, brand, setBrand, open, onToggle }) {
  const [renewBusy, setRenewBusy] = useState(false);
  const renewOnline = async () => {
    setRenewBusy(true);
    try {
      const { data } = await api.post("/activation/renew");
      if (data.applied) {
        toast.success(t("Licencja odnowiona do") + " " + (data.expires || t("bezterminowa")));
        setTimeout(() => window.location.reload(), 1400);
      } else {
        toast(data.message || t("Wysłano prośbę o odnowienie do dostawcy."));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Nie udało się wysłać prośby"));
    } finally { setRenewBusy(false); }
  };
  return (
    <Section icon={LayoutDashboard} iconColor="text-fuchsia-400" title={t("Marka i wersja (branding)")}
      open={open} onToggle={onToggle} testid="settings-sec-brand">
      <p className="text-[11px] text-slate-400 mb-3">
        {t("Dostosuj markę pod firmę ISP: nazwa produktu, kolor akcentu i tła oraz znak wodny wersji ewaluacyjnej. Logo pobierane jest z sekcji „Dane firmy”.")}
      </p>
      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Nazwa produktu")}</label>
      <input value={brand.brand_name} onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
        placeholder="FiberPass" data-testid="brand-name" className={input} />
      <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-3 block">{t("Kolor akcentu (przyciski, logo, aktywne menu)")}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={brand.brand_accent || "#2563eb"} onChange={(e) => setBrand({ ...brand, brand_accent: e.target.value })}
          data-testid="brand-accent-color" className="h-9 w-12 bg-slate-900 border border-slate-700 rounded cursor-pointer" />
        <input value={brand.brand_accent} onChange={(e) => setBrand({ ...brand, brand_accent: e.target.value })}
          placeholder={t("#2563eb (puste = domyślny)")} data-testid="brand-accent-hex" className={input} />
      </div>
      <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-3 block">{t("Kolor tła (ekran logowania / tło aplikacji)")}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={brand.brand_bg || "#0f172a"} onChange={(e) => setBrand({ ...brand, brand_bg: e.target.value })}
          data-testid="brand-bg-color" className="h-9 w-12 bg-slate-900 border border-slate-700 rounded cursor-pointer" />
        <input value={brand.brand_bg} onChange={(e) => setBrand({ ...brand, brand_bg: e.target.value })}
          placeholder={t("#0f172a (puste = domyślny)")} data-testid="brand-bg-hex" className={input} />
      </div>
      <div className="mt-4 border-t border-slate-700 pt-3">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="eval-watermark-label">
          <input type="checkbox" checked={brand.eval_watermark} onChange={(e) => setBrand({ ...brand, eval_watermark: e.target.checked })}
            data-testid="eval-watermark-toggle" className="w-4 h-4 accent-fuchsia-500" />
          <span className="text-sm text-slate-200">{t("Znak wodny „wersja ewaluacyjna” na protokołach PDF")}</span>
        </label>
        <input value={brand.eval_watermark_text} onChange={(e) => setBrand({ ...brand, eval_watermark_text: e.target.value })}
          placeholder="WERSJA EWALUACYJNA" data-testid="eval-watermark-text" className={`${input} mt-2`} disabled={!brand.eval_watermark} />
        <p className="text-[11px] text-slate-500 mt-2">{t("Gdy włączone, na wygenerowanych protokołach pojawi się dyskretny ukośny znak wodny — idealne do instalacji testowych.")}</p>
      </div>
      <div className="mt-4 border-t border-slate-700 pt-3">
        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Motyw aplikacji")}</label>
        <div className="flex gap-2">
          {["dark", "light"].map((th) => (
            <button key={th} type="button" onClick={() => setBrand({ ...brand, brand_theme: th })}
              data-testid={`brand-theme-${th}`}
              className={`px-3 py-2 rounded text-sm border ${brand.brand_theme === th ? "bg-blue-600 text-white border-blue-600" : "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800"}`}>
              {th === "dark" ? t("Ciemny") : t("Jasny")}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 border-t border-slate-700 pt-3">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-slate-300">{t("Klucz licencyjny produktu")}</label>
          {settings.licensed
            ? <span className="text-[11px] text-green-400 font-semibold" data-testid="license-badge-active">✓ {t("Licencja aktywna")}</span>
            : <span className="text-[11px] text-amber-400" data-testid="license-badge-eval">{t("Wersja ewaluacyjna")}</span>}
        </div>
        <textarea value={brand.license_key} onChange={(e) => setBrand({ ...brand, license_key: e.target.value })}
          placeholder="FPL1.xxxxxxxx.xxxxxxxx" data-testid="license-key-input" rows={2}
          className={`${input} font-mono text-[11px] resize-none`} autoComplete="off" />
        {settings.license && (
          <div className="mt-2 text-[11px] text-slate-400 space-y-0.5 bg-slate-900/50 border border-slate-800 rounded p-2" data-testid="license-details">
            {settings.license.company && <div>{t("Firma")}: <span className="text-slate-200">{settings.license.company}</span></div>}
            <div>{t("Ważność")}: <span className="text-slate-200">{settings.license.expires || t("bezterminowa")}</span></div>
            {settings.license.limits && Object.keys(settings.license.limits).length > 0 && (
              <div>{t("Limity")}: <span className="text-slate-200">{Object.entries(settings.license.limits).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span></div>
            )}
            {!settings.license.valid && settings.license.reason && (
              <div className="text-amber-400" data-testid="license-reason">{t("Status")}: {settings.license.reason}</div>
            )}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">{t("Klucz podpisany kryptograficznie (Ed25519). Poprawny, ważny klucz wyłącza znak wodny. Klucze generuje dostawca oprogramowania — nie da się ich podrobić z kodu.")}</p>
        <a href={`${API}/activation/request`} data-testid="license-renew-request"
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-cyan-400 hover:text-cyan-300 underline">
          <RefreshCw size={12} /> {t("Poproś o przedłużenie — pobierz plik .fpreq i wyślij dostawcy")}
        </a>
        <div className="mt-3 border-t border-slate-800 pt-3">
          <button type="button" onClick={renewOnline} disabled={renewBusy} data-testid="license-renew-online"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded px-3 py-2 text-sm font-medium">
            {renewBusy ? <Loader2 size={14} className="animate-spin" /> : <Wifi size={14} />} {t("Poproś / pobierz odnowienie")}
          </button>
          <p className="text-[11px] text-slate-500 mt-1.5">{t("Odnowienie jest płatne. Ten przycisk wysyła prośbę do dostawcy — po zaksięgowaniu opłaty dostawca zatwierdzi przedłużenie, a Ty klikniesz ponownie, aby pobrać nową licencję (bez plików).")}</p>
        </div>
      </div>
    </Section>
  );
}
